import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup, Validators, FormBuilder, FormArray } from "@angular/forms";
import { MatSlideToggleChange, MatSlideToggle } from "@angular/material";

import { Resource, Port } from "@impulse/sdp-shared-models";

import { ApplicationWizardPortsProtocols } from "../models";
import { ApplicationWizardService, prettyPrint } from "../application-wizard.service";
import { ValidationService } from "../../../../common/validation/validation.service";
import { TranslateService } from "../../../../common/translate/translate.service";
import { AbstractWizardStepComponent } from "../../../wizard-dialog/abstract-wizard-step.component";
import { ApplicationImageSelectComponent } from "../application-image-select/application-image-select.component";
import { ApplicationWebUrlComponent } from "../application-web-url/application-web-url.component";
import { ApplicationNetworkServiceComponent } from "../application-network-service/application-network-service.component";
import { GatewaysService } from "../../../gateways";

@Component({
	templateUrl: "./application-ports.component.html"
})
export class ApplicationPortsComponent extends AbstractWizardStepComponent<void> implements OnInit {
	public formPorts: FormArray;
	public form: FormGroup;
	public readonly addIcon = { type: "material", name: "add", cssClass: "sdp-icon-button-icon  sdp-application-ports-add-port-button" };
	public readonly deleteIcon = { type: "material", name: "delete_forever", cssClass: "sdp-icon-button-icon" };

	@ViewChild(MatSlideToggle, { static: false })
	private icmpSlideToggle!: MatSlideToggle;

	constructor(
		private service: ApplicationWizardService,
		private formBuilder: FormBuilder,
		private validationService: ValidationService,
		private translateService: TranslateService,
		private gatewayService: GatewaysService
	) {
		super();

		const ports: Resource.Port[] = this.service.getValue("ports", []);
		this.formPorts = this.formBuilder.array(ports.map((port, i) => {
			return this.createPortFormGroup(port, i);
		}));
		this.form = this.formBuilder.group({
			ports: this.formPorts,
			icmp: [
				this.service.getValue("icmp", true)
			]
		});
	}

	public async ngOnInit() {
		await this.gatewayService.getGateways();
	}

	public addPort() {
		this.formPorts.push(this.createPortFormGroup());
	}

	public deletePort(i: number) {
		this.formPorts.removeAt(i);
	}

	public get title() {
		return this.service.applicationState.isCreate ?
			"application.wizard.title.create" : "application.wizard.title.update";
	}

	public get description() {
		return this.translateService.translate("application.wizard.ports.helpText");
	}

	public get nextStep() {
		return ApplicationImageSelectComponent;
	}

	public get previousStep() {
		if (this.service.applicationState.application.selectType === "website") {
			return ApplicationWebUrlComponent;
		} else {
			return ApplicationNetworkServiceComponent;
		}
	}

	public get isFormValid() {
		return this.form.valid;
	}

	public performPrevious() {
		this.service.processPortsAndProtocols(this.formValue);
	}

	public performNext() {
		this.service.processPortsAndProtocols(this.formValue);
	}

	public get showProgressBar() {
		return false;
	}

	public get formValue(): ApplicationWizardPortsProtocols {
		if (!!this.form) {
			interface PortFormRow {
				portNumber: string;
				proto: Port.Proto;
			}

			const form = this.form.getRawValue();

			const ports = form.ports.map((row: PortFormRow) => {
				return parsePort(row.portNumber, row.proto);
			});

			return {
				ports: ports,
				icmp: form.icmp
			};
		} else {
			return {
				ports: [],
				icmp: false
			};
		}
	}

	/**
	 * true when an error message should be displayed that the ICMP setting won't work because a gateway is out of date
	 */
	public get icmpNotSupported() {
		// no toggle yet, can't be an error, we're still building the  form
		if (!this.icmpSlideToggle) {
			return false;
		}

		const minRequiredVersion = this.icmpSlideToggle.checked ? "1.2.0" : undefined;
		return this.validationService.isActiveGatewayTooOld(minRequiredVersion);
	}

	private createPortFormGroup(port?: Resource.Port, portIndex?: number) {
		let firstWebPort = false;
		// the first port for a website is locked to tcp
		if (portIndex === 0 && this.service.applicationState.application.selectType === "website") {
			firstWebPort = true;
		}

		return this.formBuilder.group({
			portNumber: [
				prettyPrint(port),
				Validators.compose([
					Validators.required,
					firstWebPort ? this.validationService.port : this.validationService.portRange,
					this.validationService.minMaxPortCheck
				])
			],
			proto: [
				{
					value: port ? port.proto : "tcp",
					disabled: firstWebPort === undefined ? false : firstWebPort
				},
				undefined
			]
		});
	}
}

function parsePort(portNumber: string, proto: Port.Proto) {
	let newPort: Resource.Port;
	if (portNumber.indexOf("-") >= 0) {
		const [min, max] = portNumber.split("-");
		newPort = {
			version: 2,
			proto: proto,
			portRange: {
				min: Number.parseInt(min, 10),
				max: Number.parseInt(max, 10)
			}
		};
	} else {
		const port = Number.parseInt(portNumber, 10);
		newPort = {
			version: 2,
			proto: proto,
			port: port,
			portNumber: port
		};
	}
	return newPort;
}
